import type {
  ActivityEntry,
  Category,
  CategoryInput,
  Chore,
  ChoreInstance,
  Countdown,
  CountdownInput,
  CountdownTask,
  EventComment,
  EventInput,
  EventInstance,
  EventRecord,
  Feed,
  FeedInput,
  List,
  ListItem,
  Meal,
  MealInput,
  Member,
  MemberLocation,
  MemberScore,
  MemberStatus,
  MemberStatusInput,
  Message,
  Household,
  InfoCard,
  InfoCardInput,
  Photo,
  Place,
  SetupStatus,
  VaultItem,
  VaultItemKind,
  VaultStatus,
  Weather,
} from '@pacer/shared';
import { http } from './client';

// --- Auth -------------------------------------------------------------------

export const getSetupStatus = () => http.get<SetupStatus>('/api/auth/setup');
export const doSetup = (passcode: string) => http.post<{ ok: true }>('/api/auth/setup', { passcode });
export const login = (passcode: string) => http.post<{ ok: true }>('/api/auth/login', { passcode });
export const logout = () => http.post<{ ok: true }>('/api/auth/logout');
export const getMe = () =>
  http.get<{ authenticated: boolean; household: { name: string; timezone: string } }>('/api/auth/me');
export const changePasscode = (current: string, next: string) =>
  http.post<{ ok: true }>('/api/auth/passcode', { current, next });

// --- Household --------------------------------------------------------------

export const getHousehold = () => http.get<Household>('/api/household');
export const updateHousehold = (patch: Partial<Household>) => http.put<Household>('/api/household', patch);

// --- Members ----------------------------------------------------------------

export const getMembers = () => http.get<Member[]>('/api/members');
export const createMember = (m: Omit<Member, 'id' | 'sortOrder' | 'avatarUrl' | 'locToken'>) =>
  http.post<Member>('/api/members', m);
export const updateMember = (id: string, m: Partial<Member>) => http.put<Member>(`/api/members/${id}`, m);
export const deleteMember = (id: string) => http.del<{ ok: true }>(`/api/members/${id}`);
export const reorderMembers = (ids: string[]) => http.put<{ ok: true }>('/api/members/reorder', { ids });

// --- Member status ("where I am") -------------------------------------------

export const getStatuses = () => http.get<MemberStatus[]>('/api/status');
export const setStatus = (memberId: string, s: MemberStatusInput) =>
  http.put<MemberStatus>(`/api/status/${memberId}`, s);
export const clearStatus = (memberId: string) => http.del<{ ok: true }>(`/api/status/${memberId}`);

// --- Locations --------------------------------------------------------------

export const getLocations = () => http.get<MemberLocation[]>('/api/locations');
export const setLocation = (memberId: string, lat: number, lon: number) =>
  http.put<MemberLocation>(`/api/locations/${memberId}`, { lat, lon });
export const clearLocation = (memberId: string) => http.del<{ ok: true }>(`/api/locations/${memberId}`);
/** `data` is a base64 string or a full data: URL. */
export const setMemberAvatar = (id: string, data: string) =>
  http.put<Member>(`/api/members/${id}/avatar`, { data });
export const removeMemberAvatar = (id: string) => http.del<Member>(`/api/members/${id}/avatar`);

// --- Categories -------------------------------------------------------------

export const getCategories = () => http.get<Category[]>('/api/categories');
export const createCategory = (c: CategoryInput) => http.post<Category>('/api/categories', c);
export const updateCategory = (id: string, c: Partial<CategoryInput>) =>
  http.put<Category>(`/api/categories/${id}`, c);
export const deleteCategory = (id: string) => http.del<{ ok: true }>(`/api/categories/${id}`);

// --- Events -----------------------------------------------------------------

/** Fetches fully-expanded occurrences for a window. `from`/`to` are ISO instants. */
export function getEvents(from: string, to: string, memberId?: string | null): Promise<EventInstance[]> {
  const q = new URLSearchParams({ from, to });
  if (memberId) q.set('memberId', memberId);
  return http.get<EventInstance[]>(`/api/events?${q}`);
}

export const getEvent = (id: string) => http.get<EventRecord>(`/api/events/${id}`);
export const createEvent = (e: EventInput & { createdBy?: string | null }) =>
  http.post<EventRecord>('/api/events', e);
export const updateEvent = (id: string, e: EventInput) => http.put<EventRecord>(`/api/events/${id}`, e);
export const deleteEvent = (id: string) => http.del<{ ok: true }>(`/api/events/${id}`);
// Comments on an event — in-context discussion ("did you mean the 8th?").
export const getEventComments = (eventId: string) =>
  http.get<EventComment[]>(`/api/events/${eventId}/comments`);
export const addEventComment = (
  eventId: string,
  c: { kind?: 'comment' | 'ack'; body?: string; authorId?: string | null },
) => http.post<EventComment>(`/api/events/${eventId}/comments`, c);
export const deleteEventComment = (eventId: string, commentId: string) =>
  http.del<{ ok: true }>(`/api/events/${eventId}/comments/${commentId}`);

/** Cancels a single occurrence of a recurring series. */
export const skipOccurrence = (id: string, occurrenceTs: string) =>
  http.post<{ ok: true }>(`/api/events/${id}/skip`, { occurrenceTs });

/** Splits one occurrence off the series as a standalone event with the given edits. */
export const detachOccurrence = (
  id: string,
  occurrenceTs: string,
  event: EventInput & { createdBy?: string | null },
) => http.post<EventRecord>(`/api/events/${id}/detach`, { occurrenceTs, event });

// --- Subscribed calendars ---------------------------------------------------

export const getFeeds = () => http.get<Feed[]>('/api/feeds');
export const createFeed = (f: FeedInput) => http.post<Feed>('/api/feeds', f);
export const updateFeed = (id: string, f: Partial<FeedInput>) => http.put<{ ok: true }>(`/api/feeds/${id}`, f);
export const deleteFeed = (id: string) => http.del<{ ok: true }>(`/api/feeds/${id}`);
export const refreshFeed = (id: string) =>
  http.post<{ ok: true; count: number }>(`/api/feeds/${id}/refresh`);

// --- Lists ------------------------------------------------------------------

export const getLists = () => http.get<List[]>('/api/lists');
export const createList = (type: 'todo' | 'shopping', name: string) =>
  http.post<{ id: string }>('/api/lists', { type, name });
export const deleteList = (id: string) => http.del<{ ok: true }>(`/api/lists/${id}`);

export const addListItem = (
  listId: string,
  text: string,
  opts: { quantity?: number; assignedMemberId?: string | null; createdBy?: string | null } = {},
) => http.post<ListItem>(`/api/lists/${listId}/items`, { text, ...opts });

export const updateListItem = (
  itemId: string,
  patch: {
    text?: string;
    done?: boolean;
    quantity?: number;
    assignedMemberId?: string | null;
    doneBy?: string | null;
  },
) => http.put<ListItem>(`/api/lists/items/${itemId}`, patch);

export const deleteListItem = (itemId: string) => http.del<{ ok: true }>(`/api/lists/items/${itemId}`);
export const clearDone = (listId: string) =>
  http.post<{ ok: true; cleared: number }>(`/api/lists/${listId}/clear-done`);
/** Previously-used items on this list, most-used first — powers one-tap re-adding. */
export const getSuggestions = (listId: string) => http.get<string[]>(`/api/lists/${listId}/suggestions`);

// --- Chores -----------------------------------------------------------------

export const getChores = (from: string, to: string) =>
  http.get<ChoreInstance[]>(`/api/chores?from=${from}&to=${to}`);
export const createChore = (c: Omit<Chore, 'id'>) => http.post<Chore>('/api/chores', c);
export const updateChore = (id: string, c: Partial<Omit<Chore, 'id'>>) =>
  http.put<Chore>(`/api/chores/${id}`, c);
export const deleteChore = (id: string) => http.del<{ ok: true }>(`/api/chores/${id}`);
export const completeChore = (id: string, dueOn: string, done: boolean, memberId?: string | null) =>
  http.post<{ ok: true }>(`/api/chores/${id}/complete`, { dueOn, done, memberId });
export const getScores = (days = 30) =>
  http.get<Array<{ memberId: string | null; points: number; completed: number }>>(
    `/api/chores/scores?days=${days}`,
  );

// --- Stats / levels / activity ----------------------------------------------

export const getMemberScores = () => http.get<MemberScore[]>('/api/stats/scores');
export const getActivity = (memberId?: string, limit = 40) => {
  const q = new URLSearchParams({ limit: String(limit) });
  if (memberId) q.set('memberId', memberId);
  return http.get<ActivityEntry[]>(`/api/stats/activity?${q}`);
};

// --- Info board -------------------------------------------------------------

export const getInfoCards = () => http.get<InfoCard[]>('/api/info');
export const createInfoCard = (c: InfoCardInput) => http.post<InfoCard>('/api/info', c);
export const updateInfoCard = (id: string, c: Partial<InfoCardInput>) =>
  http.put<InfoCard>(`/api/info/${id}`, c);
export const deleteInfoCard = (id: string) => http.del<{ ok: true }>(`/api/info/${id}`);

// --- Vault (encrypted) ------------------------------------------------------
// The PIN travels in the body over HTTPS on each call; it is never stored server-side.

export const getVaultStatus = () => http.get<VaultStatus>('/api/vault/status');
export const setupVault = (pin: string) => http.post<{ recoveryCode: string }>('/api/vault/setup', { pin });
export const unlockVault = (pin: string) => http.post<{ items: VaultItem[] }>('/api/vault/unlock', { pin });

type VaultItemInput = { title: string; kind: VaultItemKind; body: string };
export const createVaultItem = (pin: string, i: VaultItemInput) =>
  http.post<{ id: string }>('/api/vault/items', { pin, ...i });
export const updateVaultItem = (pin: string, id: string, i: VaultItemInput) =>
  http.put<{ ok: true }>(`/api/vault/items/${id}`, { pin, ...i });
export const deleteVaultItem = (pin: string, id: string) =>
  http.post<{ ok: true }>(`/api/vault/items/${id}/delete`, { pin });
export const resetVault = (newPin: string, via: { currentPin?: string; recoveryCode?: string }) =>
  http.post<{ ok: true }>('/api/vault/reset', { newPin, ...via });

// --- Meals ------------------------------------------------------------------

export const getMeals = (from: string, to: string) => http.get<Meal[]>(`/api/meals?from=${from}&to=${to}`);
/** Upsert by (date, slot). */
export const saveMeal = (m: MealInput) => http.put<Meal>('/api/meals', m);
export const deleteMeal = (id: string) => http.del<{ ok: true }>(`/api/meals/${id}`);
export const mealToGroceries = (id: string, listId: string) =>
  http.post<{ ok: true; added: number }>(`/api/meals/${id}/to-groceries`, { listId });

// --- Countdowns -------------------------------------------------------------

export const getCountdowns = () => http.get<Countdown[]>('/api/countdowns');
export const createCountdown = (c: CountdownInput) => http.post<Countdown>('/api/countdowns', c);
export const updateCountdown = (id: string, c: Partial<CountdownInput>) =>
  http.put<Countdown>(`/api/countdowns/${id}`, c);
export const deleteCountdown = (id: string) => http.del<{ ok: true }>(`/api/countdowns/${id}`);

// Plan prep checklist.
export const getCountdownTasks = (id: string) => http.get<CountdownTask[]>(`/api/countdowns/${id}/tasks`);
export const addCountdownTask = (id: string, text: string, assignedMemberId?: string | null) =>
  http.post<CountdownTask>(`/api/countdowns/${id}/tasks`, { text, assignedMemberId });
export const updateCountdownTask = (
  taskId: string,
  patch: { text?: string; done?: boolean; assignedMemberId?: string | null; doneBy?: string | null },
) => http.put<CountdownTask>(`/api/countdowns/tasks/${taskId}`, patch);
export const deleteCountdownTask = (taskId: string) =>
  http.del<{ ok: true }>(`/api/countdowns/tasks/${taskId}`);

// --- Messages (family board) ------------------------------------------------

export const getMessages = () => http.get<Message[]>('/api/messages');
export const postMessage = (body: string, authorId?: string | null) =>
  http.post<Message>('/api/messages', { body, authorId });
export const updateMessage = (id: string, patch: { body?: string; pinned?: boolean }) =>
  http.put<Message>(`/api/messages/${id}`, patch);
export const deleteMessage = (id: string) => http.del<{ ok: true }>(`/api/messages/${id}`);

// --- Push notifications ------------------------------------------------------

export const getPushKey = () => http.get<{ key: string; enabled: boolean }>('/api/push/key');
export const getPushStatus = (endpoint: string) =>
  http.get<{ subscribed: boolean }>(`/api/push/status?endpoint=${encodeURIComponent(endpoint)}`);
export const subscribePush = (subscription: PushSubscriptionJSON, memberId?: string | null) =>
  http.post<{ ok: true }>('/api/push/subscribe', { subscription, memberId });
export const unsubscribePush = (endpoint: string) =>
  http.post<{ ok: true }>('/api/push/unsubscribe', { endpoint });
export const testPush = () => http.post<{ sent: number; pruned: number }>('/api/push/test');

// --- Weather ----------------------------------------------------------------

/** Null when the household has no location set — that's a state, not an error. */
export const getWeather = () => http.get<Weather | null>('/api/weather');
export const searchPlaces = (q: string) => http.get<Place[]>(`/api/weather/search?q=${encodeURIComponent(q)}`);

// --- Photos (screensaver) ---------------------------------------------------

export const getPhotos = () => http.get<Photo[]>('/api/photos');
/** `data` is a base64 string or a full data: URL. */
export const uploadPhoto = (data: string) => http.post<Photo>('/api/photos', { data });
export const deletePhoto = (id: string) => http.del<{ ok: true }>(`/api/photos/${id}`);
